import { useEffect, useRef, useState } from 'react';
import { fetchMemberBootstrap, type MemberBootstrap } from '../lib/api/member';
import { writeMarker } from '../lib/api/http';
import { playerIdentityMode } from '../lib/api/customerContext';
import { BOOKING_DEFAULT_CONFIG } from '../engine';
import { DEFAULT_CLUB_INFO } from './useClubInfo';
import { endScopeTransition } from '../lib/api/scopeTransition';

type AnySetter = (value: any) => void;
type AnyRef = { current: any };

// ─────────────────────────────────────────────────────────────────────
// THE MEMBER APP'S MOUNT LOADER (#185).
//
// The sibling of useInitialLoad, and deliberately a SEPARATE hook rather than a
// branch inside it. That loader reads twenty-odd `/kv` keys and carries the
// §13.20 ordering rules the whole persistence layer depends on; threading a
// second mode through it would put a member path inside guarantees written for
// the club one, where a mistake shows up as data not saving.
//
// This one does the opposite of everything that loader does: ONE request, and
// NO loaded-flags flipped.
//
// WHY NO FLAGS. A flipped flag turns on that slice's blanket-persist effect,
// which writes the slice back to `/kv` on every change. A member may not write
// `/kv` — the API refuses it — so flipping them would produce a stream of 401s
// and, because the transport treats a 401 on an authenticated request as the
// session ending, would sign the member out for the crime of looking at their
// own bookings. Member writes go through the member commands instead
// (POST /member/bookings and friends), which is the next slice.
// ─────────────────────────────────────────────────────────────────────

export interface MemberBootstrapDeps {
  setAllPlayers: AnySetter;
  setBookings: AnySetter;
  setActivities: AnySetter;
  setPlannedMatches: AnySetter;
  setSingleMatches: AnySetter;
  setMemberHistory: AnySetter;
  setMemberMe: AnySetter;
  setBookingConfig: AnySetter;
  setBookingZones: AnySetter;
  setClubInfo: AnySetter;
  /** The venue's equipment and its reservations (#911 slice 2) — the store's asset slices. */
  setAssets: AnySetter;
  setAssetBookings: AnySetter;
  bookingsRef: AnyRef;
}

export type MemberBootstrapStatus = 'idle' | 'loading' | 'ready' | 'failed';

/**
 * The least time between two loads of the club before a focus may refresh it
 * (#1106). The bootstrap is the member app's heaviest read — the whole club's
 * calendar and the member's computed history — so switching back and forth
 * between apps must not send one per switch.
 */
export const MEMBER_REFRESH_MIN_MS = 30_000;

/**
 * Empty every slice the member loader writes — the values each slice starts with.
 *
 * ONE LIST, SHARED with the loader's own writes below in spirit and checked in
 * fact: `member-bootstrap-club-switch.test.tsx` asserts that every setter in
 * `MemberBootstrapDeps` is called here, so a slice added to the loader and
 * forgotten here fails a test instead of carrying one club's rows into another.
 */
export function resetClubSlices(deps: MemberBootstrapDeps): void {
  // The ref BEFORE the setter, as the loader does it: the booking handlers read
  // `bookingsRef.current` synchronously.
  deps.bookingsRef.current = [];
  deps.setAllPlayers([]);
  deps.setBookings([]);
  deps.setActivities([]);
  deps.setPlannedMatches([]);
  deps.setSingleMatches([]);
  deps.setMemberHistory([]);
  deps.setMemberMe(null);
  deps.setBookingConfig({ ...BOOKING_DEFAULT_CONFIG });
  // No zones, not an invented one (#1312): the previous club's prices must not
  // carry over, and a club with none shows «Pris ikke satt».
  deps.setBookingZones([]);
  deps.setClubInfo(DEFAULT_CLUB_INFO);
  deps.setAssets([]);
  deps.setAssetBookings([]);
}

/**
 * Write one `/member/bootstrap` answer into the store's club slices.
 *
 * Shared by the first load and the focus refresh (#1106), so the two can never
 * disagree about what an answer means.
 */
function applyBootstrap(deps: MemberBootstrapDeps, data: MemberBootstrap): void {
  // The roster the CLUB has, not the shared player table — `members` is
  // already scoped to this club by the projection (#197).
  deps.setAllPlayers(data.members);
  // The ref is updated BEFORE the setter, exactly as the club loader does
  // it: the booking handlers read `bookingsRef.current` synchronously and
  // would otherwise validate against an empty calendar for one render.
  deps.bookingsRef.current = data.bookings;
  deps.setBookings(data.bookings);
  deps.setActivities(data.activities);
  deps.setPlannedMatches(data.plannedMatches);
  deps.setSingleMatches(data.singleMatches);
  // NOT `setArchive` — see `useArchive`. These are already the member's
  // computed history, not archived tournaments, and feeding them to the
  // archive blanked the app for every member who had played one.
  deps.setMemberHistory(data.history);
  // WHO THIS MEMBER IS, keyed the way the player list is (#185). The
  // session carries the database uuid; `me.id` is the legacy id every row
  // in `effectivePlayerDB` uses, and resolving against the wrong one
  // greeted a member by another member's name.
  deps.setMemberMe(data.me);
  if (data.bookingConfig) deps.setBookingConfig(data.bookingConfig);
  // ALWAYS SET, absent included (#1312). `null` is a club that never saved
  // zones, which has no court price — keeping whatever the slice held before
  // would show a price the server will not charge.
  deps.setBookingZones(data.bookingZones ?? []);
  // THE EQUIPMENT (#911 slice 2), into the SAME slices the club portal's
  // loader fills — off the player surface this hook is inactive, so the
  // two never write them for one page. Always set, empty included: a club
  // with no machines must not show the previous club's.
  deps.setAssets(data.assets);
  deps.setAssetBookings(data.assetBookings);
  // ── UNWRAP, THEN OVERRIDE THE NAME (#766) ──────────────────
  //
  // `/member/bootstrap` answers `club: { id, name, info }` — `info`
  // being the `club_info_v1` document and `name` the canonical
  // `tenants.name`. This used to hand the WRAPPER to `setClubInfo`, so
  // every field the member app reads off `clubInfo` was undefined,
  // `name` included. Unwrapping fixes that; overriding `name` is what
  // keeps the member surface agreeing with the directory.
  //
  // A missing or non-object `info` yields the name alone rather than
  // nothing: the club is still called something even if its settings
  // document is absent or unreadable.
  if (data.club) {
    const club = data.club as { name?: unknown; info?: unknown };
    const info =
      club.info && typeof club.info === 'object' && !Array.isArray(club.info)
        ? (club.info as Record<string, unknown>)
        : {};
    deps.setClubInfo(
      typeof club.name === 'string' && club.name ? { ...info, name: club.name } : info,
    );
  }
}

/**
 * Loads the member's club when a member session is driving the app, and loads
 * it again, silently, when the app comes back into view (#1106).
 *
 * Returns the status so the shell can tell «still loading» from «this failed»,
 * rather than rendering an empty club and letting the member conclude their
 * bookings are gone.
 */
export function useMemberBootstrap(
  deps: MemberBootstrapDeps,
  /**
   * The ACTIVE CLUB CONTEXT an account has selected, or null for none.
   *
   * Ignored for a legacy member session, whose club is the session's own and
   * cannot change under it. For an account it is the whole question: with no
   * club selected there is nothing club-scoped to fetch, and the player app
   * stands on the account alone.
   */
  activeClubId: string | null = null,
): {
  status: MemberBootstrapStatus;
  error: string | null;
  active: boolean;
  /** Load the club again, after a failure. */
  retry: () => void;
  /** Whether the store's club slices are the ACTIVE club's — see below (#848 review). */
  clubDataVisible: boolean;
  /**
   * False when this club has not saved its opening hours, so no booking can be
   * made there yet (owner decision 2026-10-31). True until the club has
   * answered, and off the player surface — nothing is withheld on a guess.
   */
  bookingOpen: boolean;
} {
  // ── THE SOURCE IS THE SURFACE'S, NOT THE CREDENTIAL'S (#800) ──────
  //
  // `playerIdentityMode()` resolves the surface first: a legacy member session
  // loads its own club; an account loads the club it has SELECTED, and nothing
  // until it has selected one. The surface is fixed for the life of the page,
  // so this is read once.
  const [mode] = useState(() => playerIdentityMode());

  // ── THE CLUB FOLLOWS THE SELECTION, IN PLACE (player-first shell) ──
  //
  // This used to be read ONCE and changed by REMOUNT: the store was keyed on
  // the entered club, so every hook below it — and the player app with it —
  // was torn down and rebuilt on any change of club. The owner's shell rule is
  // that the player app SURVIVES a change of club context: same navigation,
  // same tab, only the club's data reloads. So the club is now a dependency,
  // and the guarantee the key used to give wholesale is given here explicitly:
  //
  //   1. NOTHING OF THE PREVIOUS CLUB SURVIVES A CHANGE. Every slice this hook
  //      writes is reset to its empty value before the next club is fetched,
  //      so no consumer can render club A's rows under club B's name — even a
  //      consumer that ignores the status.
  //   2. THE STATUS IS PER CLUB. It is derived from which club the loaded data
  //      BELONGS to, not from a flag, so on the very render after a change it
  //      already says «loading» rather than «ready» for data that is not this
  //      club's. A flag set in an effect would be one frame late.
  //   3. A LATE ANSWER FOR THE OLD CLUB IS DROPPED. Each fetch is cancelled
  //      when the club changes, so a slow response for A cannot land after B
  //      was selected.
  //
  // The transport addresses the club through `selectedPlayerClub()`, which the
  // gate sets BEFORE it publishes the new selection — so the fetch this
  // triggers already carries the new club's tenant header. The server still
  // checks the roster row for that club on every request: selecting a club
  // grants nothing.
  //
  // ── AND NEVER THE CLUB PORTAL'S ──────────────────────────────────
  //
  // Off the player surface (the club portal, the memory backend) this hook is
  // inactive and a DIFFERENT loader fills these same slices; a reset here would
  // race it and could wipe a club's data. That is guaranteed by THIS line, not
  // by a guard in the effect: off the player surface the key is null whatever
  // `activeClubId` says, so it can never change, and a reset only ever happens
  // on a change. A separate guard was written first and a mutation removing it
  // survived — because it could never fire. The property lives here.
  const activeKey: string | null =
    mode === 'member-session' ? 'member-session' : mode === 'account' ? activeClubId : null;
  const active = activeKey != null;

  // ── A SELECTION, NOT A CLUB ID (#848 re-review) ──────────────────
  //
  // `loaded` used to record only WHICH CLUB answered. So A(ready) → B
  // (pending) → back to A, before B answered, matched the old A marker by id:
  // `ready` and `clubDataVisible` at once, over slices the B transition had
  // already emptied and before the NEW A request had answered — an apparently
  // loaded A with an empty calendar, and its actions unblocked.
  //
  // Every change of key is a new SELECTION, numbered here, and an answer
  // counts only for the selection it was requested in. Numbered DURING RENDER
  // (React's «derive state from the previous render» pattern), not in the
  // effect: the render that returns to A must already see that A's old answer
  // is not this selection's, or that one render is the bug.
  const [selection, setSelection] = useState<{ key: string | null; seq: number }>({
    key: activeKey,
    seq: 0,
  });
  let seq = selection.seq;
  if (selection.key !== activeKey) {
    seq = selection.seq + 1;
    setSelection({ key: activeKey, seq });
  }

  /** What the slices currently hold, and for which SELECTION. */
  const [loaded, setLoaded] = useState<{
    key: string;
    seq: number;
    status: 'ready' | 'failed';
    error: string | null;
    /** The server's `bookingOpen` for this selection (owner decision 2026-10-31). */
    bookingOpen: boolean;
  } | null>(null);
  const [attempt, setAttempt] = useState(0);
  /** The club the slices were last filled for — so a reset happens only on a CHANGE. */
  const previousKey = useRef<string | null | undefined>(undefined);
  /** When the club was last ASKED for — by the load or by a refresh. */
  const lastAskedAt = useRef(0);

  useEffect(() => {
    // (1) — on a CHANGE of club, empty every slice before anything else. Not
    // on the first run: the slices already hold their empty values, and
    // writing them again is a render for nothing.
    const changed = previousKey.current !== undefined && previousKey.current !== activeKey;
    previousKey.current = activeKey;
    if (changed) resetClubSlices(deps);
    if (!activeKey) return undefined;

    let cancelled = false;
    const key = activeKey;
    // This render's selection — the one the request below answers for.
    const requestSeq = seq;
    lastAskedAt.current = Date.now();
    (async () => {
      try {
        const data = await fetchMemberBootstrap();
        // (3) — an answer for a club that is no longer the selection.
        if (cancelled) return;
        applyBootstrap(deps, data);
        setLoaded({
          key,
          seq: requestSeq,
          status: 'ready',
          error: null,
          bookingOpen: data.bookingOpen,
        });
        // THE PLAYER APP HAS ITS CLUB'S DATA: a surface or club switch that
        // was in progress is over, and writes may flow again
        // (lib/api/scopeTransition.ts). A no-op when none was.
        endScopeTransition();
      } catch (e) {
        if (cancelled) return;
        setLoaded({
          key,
          seq: requestSeq,
          status: 'failed',
          error: e instanceof Error ? e.message : 'ukjent feil',
          bookingOpen: true,
        });
        // A failed load is still the end of the transition: the app is on
        // this club, and keeping it closed would help nobody.
        endScopeTransition();
      }
    })();
    return () => {
      cancelled = true;
    };
    // The club and the EXPLICIT retry (#800) — nothing else. The surface and
    // the session do not change under a running page.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeKey, attempt]);

  // (2) — derived, so it is right on the render after a change, not one later.
  const current = loaded && loaded.key === activeKey && loaded.seq === seq ? loaded : null;
  const status: MemberBootstrapStatus = !active ? 'idle' : current ? current.status : 'loading';

  // ── AND AGAIN WHEN THE APP COMES BACK INTO VIEW (#1106) ───────────
  //
  // The club used to be loaded ONCE, so whatever changed on the server after
  // that — a payment's webhook, a hold released, a booking the club cancelled,
  // the member's own action on another device — was invisible until the app
  // was restarted. A tester paid a court and the reservation kept saying
  // «Ikke betalt» and counting down its hold.
  //
  // So a return to the app re-reads the club, and the answer replaces the
  // slices IN PLACE: the status stays `ready`, nothing is emptied first and no
  // spinner shows, because the data on screen was right a moment ago and is
  // only being brought up to date. What it may not do:
  //
  //   - RUN BEFORE THE CLUB HAS LOADED. A load that is pending or failed is
  //     the first load's (and `retry`'s) business; a refresh then would race
  //     it and could turn «failed» into a club nobody asked to reload.
  //   - LAND ON ANOTHER SELECTION. The listener belongs to one selection and
  //     is torn down with it, so an answer for club A after a switch to B is
  //     dropped, as the first load's is.
  //   - OVERWRITE THE MEMBER'S OWN WRITE. An answer that may predate a write
  //     this page sent while it was in flight is dropped (`writeMarker`): the
  //     read could have left before the booking was committed, and adopting
  //     it would make the booking vanish until the next refresh.
  //   - FAIL OUT LOUD. A failed refresh keeps what is on screen; the next
  //     return to the app asks again.
  const refreshable = useRef(false);
  refreshable.current = active && current?.status === 'ready';
  useEffect(() => {
    if (!activeKey || typeof document === 'undefined') return undefined;
    let cancelled = false;
    let running = false;
    const refresh = async () => {
      if (cancelled || running || !refreshable.current) return;
      if (document.visibilityState === 'hidden') return;
      if (Date.now() - lastAskedAt.current < MEMBER_REFRESH_MIN_MS) return;
      const before = writeMarker();
      if (before === null) return;
      running = true;
      lastAskedAt.current = Date.now();
      try {
        const data = await fetchMemberBootstrap();
        if (cancelled || !refreshable.current) return;
        const after = writeMarker();
        if (after === null || after !== before) return;
        applyBootstrap(deps, data);
        // The club may have saved its hours since — the same answer, in place.
        setLoaded((l) =>
          l && l.key === activeKey && l.seq === seq ? { ...l, bookingOpen: data.bookingOpen } : l,
        );
      } catch {
        // Keep what is on screen — see above.
      } finally {
        running = false;
      }
    };
    const onReturn = () => {
      void refresh();
    };
    document.addEventListener('visibilitychange', onReturn);
    window.addEventListener('focus', onReturn);
    return () => {
      cancelled = true;
      document.removeEventListener('visibilitychange', onReturn);
      window.removeEventListener('focus', onReturn);
    };
    // One listener per SELECTION — see «LAND ON ANOTHER SELECTION» above.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeKey, seq]);

  // ── WHETHER THE CLUB DATA IN THE STORE IS THIS CLUB'S — AT RENDER TIME ──
  //
  // #848 review. The reset in (1) runs in an EFFECT, i.e. AFTER the render
  // that first sees a new club — so on that render, and on every render until
  // B answers, the slices could still hold club A's roster, bookings and
  // history under B's name (or under «no club»). The shell deliberately keeps
  // Hjem, Profil and the sub-screens on screen through a switch, so they
  // would have shown A's data.
  //
  // So visibility is DERIVED, like `status`: the slices count as this club's
  // only once THIS key's own answer has arrived. B's slices and its `ready`
  // are written in the same async tick, so they reach the screen in the same
  // render — there is no frame where B is `ready` and A's data is visible.
  // `StoreProvider` masks every club slice while this is false.
  //
  // Off the player surface the slices are another loader's (the club
  // portal's) and are always the store's own.
  const clubDataVisible = mode === 'picked' || current?.status === 'ready';

  return {
    status,
    error: current?.error ?? null,
    active,
    clubDataVisible,
    bookingOpen: current?.bookingOpen ?? true,
    /**
     * Load the club again (#800).
     *
     * The status has been reported since #185 and NOTHING CONSUMED IT, so a
     * failed bootstrap rendered an empty club — exactly what this hook's own
     * header says it exists to prevent: «rather than rendering an empty club
     * and letting the member conclude their bookings are gone». A status with
     * no reader and no remedy is a comment.
     */
    retry: () => {
      setLoaded(null);
      setAttempt((a) => a + 1);
    },
  };
}
